import { Attachment, LocalShipping } from "@mui/icons-material";
import ClearIcon from "@mui/icons-material/Clear";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { useHistoryState } from "@uidotdev/usehooks";
import cuid from "cuid";
import type React from "react";
import { useContext, useRef, useState } from "react";
import { useDrop } from "react-aria";

import FilePreview from "@/components/FeedItem/FilePreview";
import PaperAirplane from "@/components/Icons/PaperAirplane";
import SendIcon from "@/components/Icons/Send";
import TemplateIcon from "@/components/Icons/TemplateIcon";
import TextMessageInput from "@/components/TextMessageInput";
import AudioLevelIndicator from "@/components/Visualization/AudioLevelIndicator.tsx";
import { client } from "@/config";
import useLocationChange from "@/hooks/useLocationChange";
import Locator from "@/locator";
import { AppContext } from "@/models/AppStateProvider.tsx";
import { publishToWorkspaceFeed } from "@/models/actions/clientActions";
import { DataContext } from "@/models/DataProvider";
import { FeedContext } from "@/models/FeedContextProvider";
import { CurrentFeedContext } from "@/models/StateProviders/currentFeedProvider";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { actions, TelemetryContext } from "@/models/TelemetryProvider";
import { TrackingContext } from "@/models/TrackingStateProvider";
import { UppyContext } from "@/models/UppyContextProvider";
import { UxContext } from "@/models/UxStateProvider";
import { fileUploadConfig } from "@/utils/config";
import TemplatesModal from "../Templates/TemplatesModal.tsx";
import UploadFileButton from "../UploadFiles/UploadFileButton";

type PastedFile = File & {
  url: string;
  name: string;
  data: File | Blob;
  fileId: string;
};

const ttsPlaceholder = "Start typing your message";

export default function TTSForm({
  disableControls,
  makeCallButton,
  makeRecording,
  recordingProps,
  cancelRecording,
  saveRecording,
}: {
  disableControls?: boolean;
  makeCallButton?: React.ReactNode;
  makeRecording?: React.ReactNode;
  cancelRecording?: () => void;
  saveRecording?: () => void;
  recordingProps?: {
    processing: boolean;
    progress: number;
    disabled: boolean;
    showAudioLevels: boolean;
    audioLevel: number;
    time: string;
    uploading: boolean;
    color: string;
    uploadProgress: number;
  };
}) {
  const theme = useTheme();
  const { preferredLanguage } = useContext(DataContext);
  const { currentFeed, currentFeedId } = useContext(CurrentFeedContext);
  const { currentWorkspaceId, isWorkspaceLimitedMember } = useContext(WorkspaceContext);
  const { setTemplatesModal, templatesModal, setStatus: setFeedStatus, scrollToBottomOfFeed } = useContext(FeedContext);
  const { ampli } = useContext(TrackingContext);
  const { uppyClient } = useContext(UppyContext);
  const { isSmUp, setFleetMessageModalOpen } = useContext(UxContext);
  const {
    state: textState,
    set: setText,
    undo: undoText,
    redo: redoText,
    clear: clearText,
    canUndo: canUndoText,
    canRedo: canRedoText,
  } = useHistoryState({ textInputValue: "" });
  const [loading, setLoading] = useState<boolean>(false);
  const [files, setFiles] = useState<PastedFile[]>([]);
  const [groupId, setGroupId] = useState<string>(cuid());
  const textInputRef = useRef(null);
  const limitedMember = isWorkspaceLimitedMember();
  const { trackAction } = useContext(TelemetryContext);
  const { flags } = useContext(AppContext);

  const workspaceId = currentWorkspaceId;
  const feedId = currentFeedId;

  const maxChars = 5000;

  const exceededCharLimit = textState.textInputValue.length > maxChars;
  const canSendMessage = (textState.textInputValue?.length > 0 || files.length > 0) && !exceededCharLimit;

  const disabled = loading || disableControls;
  const fileTypes =
    fileUploadConfig.allowedMimeTypes.length > 0
      ? fileUploadConfig.allowedMimeTypes?.filter(
          (at) => at.startsWith("image") || at.startsWith("application") || at.startsWith("text"),
        )
      : ["image/jpeg", "image/png", "image/svg+xml", "image/webp", "image/gif", "image/bmp", "application/pdf"];

  useLocationChange(() => {
    clearText();
  });

  const removeFile = (index) => {
    if (files.length > 0) {
      const newFiles = files.filter((file, i) => i !== index);
      if (uppyClient) {
        const file = files[index];
        uppyClient.removeFile(file?.fileId);
      }
      setFiles(newFiles);
    }
  };

  const reset = () => {
    setLoading(false);
    clearText();
    setFiles([]);
    uppyClient?.setOptions({ restrictions: { maxNumberOfFiles: 1 } });
    setGroupId(cuid());
    setTimeout(() => textInputRef.current?.focus(), 50);
  };

  const sendTextMessage = async () => {
    if (canSendMessage) {
      const contentId = cuid();
      try {
        setLoading(true);
        setFeedStatus({ message: "Sending message...", severity: "info" });
        if (textState.textInputValue.length > 0) {
          ampli.textToSpeechMessageSend();

          client
            .createContentEvent({
              workspaceId,
              contentId,
              step: "client_uploading",
              status: "started",
              feedId,
              context: {
                type: "tts_message",
                characterLength: textState.textInputValue?.length ?? 0,
              },
            })
            .then(() => {});

          const itemId = cuid();
          trackAction(actions.feedItemTextTts({ id: itemId, feedId }), {
            workspaceId,
            feedId,
            contentId,
            itemId,
            text: textState?.textInputValue,
            preferredLanguage,
            isSilent: currentFeed?.isSilent,
          });
          trackAction(actions.feedItemAudioTts({ id: itemId, feedId }), {
            workspaceId,
            feedId,
            contentId,
            itemId,
            text: textState?.textInputValue,
            preferredLanguage,
            isSilent: currentFeed?.isSilent,
          });

          await publishToWorkspaceFeed({
            workspaceId,
            feedId,
            contentId,
            groupId,
            itemId,
            text: textState?.textInputValue,
            preferredLanguage,
            isSilent: currentFeed?.isSilent,
          });

          client
            .createContentEvent({
              workspaceId,
              contentId,
              step: "client_uploading",
              status: "finished",
              feedId,
              context: {
                type: "tts_message",
                characterLength: textState.textInputValue?.length ?? 0,
              },
            })
            .then(() => {});
        }

        if (files.length > 0) {
          // Start the upload
          await uppyClient?.upload();
        }
        setFeedStatus(null);
        // after sending message, scroll to bottom of the feed if present
        scrollToBottomOfFeed();
      } catch (error) {
        setFeedStatus({ message: "Failed to send message", severity: "error" });
        client
          .createContentEvent({
            workspaceId,
            contentId,
            step: "client_uploading",
            status: "failed",
            feedId,
            context: {
              type: "tts_message",
              characterLength: textState.textInputValue?.length ?? 0,
            },
            error,
          })
          .then(() => {});
        return Promise.reject(error);
      } finally {
        reset();
      }
    }
  };

  const handleNewFileAdded = (file: File) => {
    if (file) {
      const url = URL.createObjectURL(file);
      const newFile = {
        url,
        name: file.name,
        type: file.type,
        data: file,
      } as PastedFile;
      const addedId = uppyClient.addFile({
        source: "TTSForm",
        meta: { groupId: groupId },
        ...newFile,
      });
      newFile.fileId = addedId || "";
      setFiles((prev) => [...prev, newFile]);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const items = Array.from((event.clipboardData || event.originalEvent.clipboardData).items) as DataTransferItem[];

    if (items) {
      uppyClient?.setOptions({ restrictions: { maxNumberOfFiles: null } });
      items.map(async (item) => {
        if ((item.kind === "file" || item.kind === "image") && fileTypes.includes(item.type)) {
          const file = item.getAsFile();
          handleNewFileAdded(file);
        }

        // Only accept plain text values for this input type
        if (item.kind === "string" && item.type === "text/plain") {
          const cursorStartPosition = event.target.selectionStart || 0;
          const cursorEndPosition = event.target.selectionEnd || textState?.textInputValue?.length;
          item.getAsString((text) => {
            handleTextInput(
              textState?.textInputValue.slice(0, cursorStartPosition) +
                text +
                textState?.textInputValue.slice(cursorEndPosition),
            );
            // "setTimeout" to update caret after setting the text
            window.requestAnimationFrame(() => {
              textInputRef.current?.setSelectionRange(
                cursorStartPosition + text.length,
                cursorStartPosition + text.length,
              );
            });
          });
        }
      });
    }
  };

  const handleFleetMessageOpen = () => {
    ampli.openFleetMessage({ workspaceId });
    setFleetMessageModalOpen(true);
  };

  const { dropProps } = useDrop({
    ref: textInputRef,
    async onDrop(e) {
      console.log("onDrop", e);
      if (e.items) {
        uppyClient?.setOptions({ restrictions: { maxNumberOfFiles: null } });
        e.items.map(async (item) => {
          if (item.kind === "file" && fileTypes.includes(item.type)) {
            const file = await item.getFile();
            handleNewFileAdded(file);
          }

          if (item.kind === "text") {
            const textType = Array.from(item.types.values())?.[0];
            const text = await item.getText(textType || "text/plain");
            handleTextInput(textState?.textInputValue + text);
          }
        });
      }
    },
  });

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement>) => {
    switch (event.code) {
      case "Enter":
      case "NumpadEnter":
        if (event.shiftKey) break;
        event.preventDefault();
        await sendTextMessage();
        break;
      case "KeyZ":
        if (event.ctrlKey || event.metaKey) {
          if (event.shiftKey) {
            if (canRedoText) {
              redoText();
            }
          } else {
            if (canUndoText) {
              undoText();
            }
          }
        }
        break;
    }
  };

  const handleTextInput = (text: string) => {
    setText({ textInputValue: text });
  };

  const handleTemplateSelection = (template: string) => {
    handleTextInput(template);
    setTimeout(() => textInputRef.current?.focus(), 50);
    setTemplatesModal(false);
  };

  const handleOpenTemplates = () => {
    setTemplatesModal(() => true);
    ampli.openTemplatesFeed();
  };

  const hasAudioState =
    recordingProps?.progress > 0 || recordingProps?.uploading || recordingProps?.processing || recordingProps?.disabled;

  const footerButtonSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: 1,
    width: "auto",
    minWidth: "none",
    color: "var(--color-zinc-300)",
    textTransform: "none",
    letterSpacing: "0.05em",
    bgcolor: "var(--color-zinc-700)",
    borderRadius: 2,
    fontSize: "1rem",
    px: 1,
    py: 0.5,
    "&:hover": { color: "#fff", bgcolor: "var(--color-zinc-600)" },
  } as const;

  if (flags.newDesign) {
    return (
      <>
        <Box
          sx={{
            bgcolor: "var(--color-zinc-800)",
            borderRadius: 1,
            border: "1px solid var(--color-zinc-400)",
            p: 1,
            width: "100%",
            height: "100%",
            position: "relative",
          }}
        >
          {hasAudioState && (
            <Box
              sx={{
                position: "absolute",
                zIndex: 30,
                left: 0,
                top: 0,
                width: "100%",
                height: "100%",
                bgcolor: "rgba(39, 39, 42, 0.8)",
                p: 2,
                borderRadius: 1,
                backdropFilter: "blur(2px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              style={{ boxShadow: `0 0 0 2px ${recordingProps?.color}` }}
            >
              {recordingProps.uploadProgress > 0 && (
                <Box sx={{ textAlign: "center", minWidth: 200 }}>
                  <Box>Uploading {recordingProps.uploadProgress}%</Box>
                  <Box
                    sx={{
                      bgcolor: "var(--color-zinc-700)",
                      p: 0.5,
                      borderRadius: 1,
                      mt: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        height: 4,
                        background: "linear-gradient(90deg, #4ade80, #16a34a)",
                        borderRadius: 1,
                        width: `${recordingProps.uploadProgress}%`,
                        transition: "width 0.2s ease-out",
                      }}
                    />
                  </Box>
                </Box>
              )}

              {recordingProps.processing && recordingProps.uploadProgress === 0 && (
                <Box sx={{ textAlign: "center" }}>Processing audio...</Box>
              )}

              {!recordingProps.uploading &&
                !recordingProps.processing &&
                recordingProps.disabled &&
                recordingProps.progress === 0 && <Box sx={{ textAlign: "center" }}>Processing Request</Box>}

              {recordingProps.progress > 0 && (
                <>
                  <Box sx={{ textAlign: "center" }}>
                    <Box
                      sx={{
                        bgcolor: "var(--color-brand-primary-500)",
                        color: "#fff",
                        p: 1,
                        borderRadius: "9999px",
                        mb: 1,
                      }}
                    >
                      <AudioLevelIndicator
                        audioLevel={recordingProps.audioLevel}
                        color={theme.palette.primary.main}
                        size={30}
                      />
                    </Box>
                    <Box>{recordingProps.time}</Box>
                  </Box>
                  <Button
                    type="button"
                    onClick={() => cancelRecording()}
                    sx={{
                      position: "absolute",
                      right: "110px",
                      bottom: "11px",
                      boxShadow: "0 0 0 2px var(--color-zinc-500)",
                      borderRadius: "50%",
                      minWidth: 0,
                      width: "auto",
                      bgcolor: "var(--color-zinc-700)",
                      "&:hover": { bgcolor: "var(--color-zinc-600)" },
                    }}
                  >
                    <ClearIcon role="img" sx={{ fontSize: 20 }} />
                  </Button>
                  <Button
                    type="button"
                    onClick={() => saveRecording()}
                    sx={{
                      position: "absolute",
                      right: "52px",
                      bottom: "11px",
                      boxShadow: "0 0 0 2px #4ade80",
                      borderRadius: "50%",
                      minWidth: 0,
                      width: "auto",
                      bgcolor: "#16a34a",
                      "&:hover": { bgcolor: "#22c55e" },
                    }}
                  >
                    <SendIcon width={18} height={18} />
                  </Button>
                </>
              )}
            </Box>
          )}
          <Box sx={{ height: "66.666667%" }}>
            <Box sx={{ height: "100%", pb: 1 }}>
              <Box
                className="footer-buttons"
                sx={{
                  borderBottom: "1px solid var(--color-zinc-400)",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  position: "relative",
                }}
              >
                <Box sx={{ flexGrow: 1, height: "100%" }}>
                  <TextMessageInput
                    inputId="tts-form-text-input"
                    inputRef={textInputRef}
                    inputProps={{
                      autoFocus: true,
                      onKeyDown: (e) => handleKeyDown(e),
                      onPaste: (e) => handlePaste(e),
                    }}
                    sx={{
                      padding: 0,
                      width: "100%",
                      height: "100%",
                      ".MuiInputBase-input": {
                        margin: 0,
                        border: "none",
                        borderRadius: 0,
                        height: "100%",
                      },
                    }}
                    rows={1}
                    minRows={1}
                    placeholder={ttsPlaceholder}
                    aria-label={Locator.feed.input.tts}
                    disabled={disabled}
                    textInputValue={textState?.textInputValue}
                    textInputHandler={handleTextInput}
                    showHelperText={false}
                  />
                </Box>

                {textState?.textInputValue?.length > 0 && (
                  <Box
                    className="tts-helper"
                    sx={{
                      fontSize: "0.75rem",
                      color: "var(--color-zinc-300)",
                      textAlign: "right",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Box>Enter to send message</Box>
                    <Box>Shift+Enter to create new line</Box>
                    <Box sx={{ color: "var(--color-zinc-400)" }}>
                      {textState?.textInputValue?.length} / {maxChars}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          {files.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                overflowX: "auto",
                px: 0.5,
                py: 0.5,
              }}
            >
              {files.map((file, index) => (
                <FilePreview
                  key={`file-preview-${file.name}`}
                  index={index}
                  url={file.url}
                  type={file.type}
                  name={file.name}
                  disabledRemove={disabled}
                  removeFileCallback={(i) => removeFile(i)}
                />
              ))}
            </Box>
          )}
          <Box
            sx={{
              height: "33.333333%",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              className="footer-buttons"
              sx={{
                flexGrow: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Button type="button" className="footer-button" onClick={handleOpenTemplates} sx={footerButtonSx}>
                <span className="icon">
                  <TemplateIcon />
                </span>
                <span className="text">Templates</span>
              </Button>

              <UploadFileButton
                button={
                  <Button type="button" className="footer-button" sx={footerButtonSx}>
                    <span className="icon">
                      <Attachment role="presentation" />
                    </span>
                    <span className="text">Files</span>
                  </Button>
                }
              />

              {!limitedMember && (
                <Button type="button" className="footer-button" onClick={handleFleetMessageOpen} sx={footerButtonSx}>
                  <span className="icon">
                    <LocalShipping />
                  </span>
                  <span className="text">Fleet Message</span>
                </Button>
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {makeCallButton}
              {makeRecording}
              <Button
                type="button"
                disabled={!canSendMessage}
                aria-label={Locator.feed.input.send}
                onClick={sendTextMessage}
                sx={{
                  borderRadius: "50%",
                  minWidth: 0,
                  width: 38,
                  height: 38,
                  p: 0,
                  ...(canSendMessage
                    ? {
                        bgcolor: "var(--color-brand-primary-500)",
                        "&:hover": {
                          bgcolor: "var(--color-brand-primary-600)",
                        },
                      }
                    : {
                        bgcolor: "var(--color-zinc-700)",
                        "&:hover": { bgcolor: "var(--color-zinc-600)" },
                      }),
                }}
              >
                <PaperAirplane
                  style={{
                    fill: !canSendMessage ? theme.palette.neutral.main : theme.palette.primary.main,
                  }}
                />
              </Button>
            </Box>
          </Box>
        </Box>
        <TemplatesModal open={templatesModal} useSelectedTemplate={handleTemplateSelection} />
      </>
    );
  }

  return (
    <Stack
      {...dropProps}
      sx={{
        width: "100%",
        height: "100%",
        gap: 1,
        position: "relative",
      }}
    >
      <Box
        sx={{
          px: 2.25,
          py: 2.5,
          height: "auto",
          position: "relative",
          background: theme.palette.secondary.dark,
          borderRadius: "24px",
          boxShadow: "0px 2px 8px 0px #04070540",
          border: `1px solid transparent`,
          borderColor: exceededCharLimit ? theme.palette.error.main : "transparent",
          "&:focus-within": {
            borderColor: theme.palette.primary.main,
          },
        }}
      >
        <TextMessageInput
          inputId="tts-form-text-input"
          inputRef={textInputRef}
          inputProps={{
            autoFocus: true,
            onKeyDown: (e) => handleKeyDown(e),
            onPaste: (e) => handlePaste(e),
          }}
          className="text-input-v2"
          rows={0}
          minRows={1}
          placeholder="Start typing your message"
          aria-label={Locator.feed.input.tts}
          disabled={disabled}
          textInputValue={textState?.textInputValue}
          textInputHandler={handleTextInput}
          sx={{
            ".MuiInputBase-input": {
              width: "100%",
              [theme.breakpoints.up("md")]: { paddingRight: "0px !important" },
              [theme.breakpoints.up("lg")]: {
                paddingRight: "200px !important",
              },
              borderRadius: 0,
              height: "100%",
              ":focus-visible": {
                boxShadow: "none",
              },
            },
            "&.text-input-v2 textarea": {
              minHeight: 44,
              maxHeight: 200,
              overflowY: "auto !important",
              padding: 0,
            },
          }}
          showHelperText={false}
        />

        <Box
          sx={(theme) => ({
            fontSize: "14px",
            position: "absolute",
            right: "1rem",
            top: "0.7rem",
            textAlign: "right",
            width: "200px",
            color: theme.palette.neutral.main,
            [theme.breakpoints.up("xs")]: {
              padding: "5px 10px",
              right: 0,
              borderRadius: "0.5rem",
              background: theme.palette.secondary.dark,
              width: "auto",
              top: "-60px",
            },
            [theme.breakpoints.up("md")]: {
              padding: "5px 10px",
              right: 0,
              borderRadius: "0.5rem",
              background: theme.palette.secondary.dark,
              width: "auto",
              top: "-60px",
            },
            [theme.breakpoints.up("lg")]: {
              background: "transparent",
              top: "0.7rem",
            },
          })}
        >
          <Box>Enter to send message</Box>
          <Box>Shift+Enter to create new line</Box>
        </Box>
        <Stack
          sx={{
            borderTop: `1px solid ${theme.palette.neutral.main}`,
            pt: 2,
            gap: 2,
          }}
        >
          {files?.length > 0 ? (
            <Stack
              sx={{
                width: "100%",
                flexDirection: "row",
                alignItems: "center",
                gap: 2,
                overflowX: "auto",
                paddingTop: "10px",
              }}
            >
              {files.map((file, index) => {
                return (
                  <FilePreview
                    key={`file-preview-${file.name}`}
                    index={index}
                    url={file.url}
                    type={file.type}
                    name={file.name}
                    disabledRemove={disabled}
                    removeFileCallback={(i) => removeFile(i)}
                  />
                );
              })}
            </Stack>
          ) : null}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: { xs: "column", sm: "row" },
              width: "100%",
              gap: 2,
            }}
            className="tts-form-buttons-container"
          >
            <Box
              className="tts-form-buttons"
              sx={{
                flexGrow: 1,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              {!limitedMember ? (
                <Button
                  type="button"
                  className="tts-form-button"
                  onClick={handleOpenTemplates}
                  sx={{
                    display: "flex",
                    background: theme.palette.secondary.main,
                    alignItems: "center",
                    justifyItems: "center",
                    borderRadius: isSmUp ? 6 : "100%",
                    width: isSmUp ? "auto" : 44,
                    height: isSmUp ? "auto" : 44,
                    minWidth: 0,
                    px: isSmUp ? 2 : 1,
                    gap: 1,
                  }}
                  disabled={disabled}
                  aria-label={Locator.feed.input.templates.main}
                >
                  <Box className="tts-button-details">
                    <TemplateIcon />
                    {isSmUp && (
                      <Box className="tss-form-button-text" sx={{ textTransform: "none" }}>
                        Templates
                      </Box>
                    )}
                  </Box>
                </Button>
              ) : null}
              <UploadFileButton
                className="tts-form-button"
                sx={{
                  display: "flex",
                  background: theme.palette.secondary.main,
                  alignItems: "center",
                  justifyItems: "center",
                  borderRadius: isSmUp ? 6 : "100%",
                  width: isSmUp ? "auto" : 44,
                  height: isSmUp ? "auto" : 44,
                  minWidth: 0,
                  px: isSmUp ? 2 : 1,
                  gap: 1,
                }}
                disabled={disabled}
              >
                <Box className="tts-button-details">
                  <Attachment role="presentation" />
                  {isSmUp && (
                    <Box className="tss-form-button-text" sx={{ textTransform: "none" }}>
                      Files
                    </Box>
                  )}
                </Box>
              </UploadFileButton>
              {!limitedMember ? (
                <Button
                  className="tts-form-button"
                  type="button"
                  onClick={handleFleetMessageOpen}
                  sx={{
                    display: "flex",
                    background: theme.palette.secondary.main,
                    alignItems: "center",
                    justifyItems: "center",
                    borderRadius: isSmUp ? 6 : "100%",
                    width: isSmUp ? "auto" : 44,
                    height: isSmUp ? "auto" : 44,
                    minWidth: 0,
                    px: isSmUp ? 2 : 1,
                    gap: 1,
                  }}
                  disabled={disabled}
                  aria-label={Locator.feed.input.fleetMessage.main}
                >
                  <Box className="tts-button-details">
                    <LocalShipping />
                    {isSmUp && (
                      <Box className="tss-form-button-text" sx={{ textTransform: "none" }}>
                        Fleet message
                      </Box>
                    )}
                  </Box>
                </Button>
              ) : null}
            </Box>
            <Stack
              className="tts-form-submit"
              sx={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              {exceededCharLimit ? (
                <Typography className="exceeding-limit" sx={{ color: theme.palette.error.main }}>
                  Exceeding character limit
                </Typography>
              ) : null}
              <Typography
                className="character-count"
                sx={{
                  color: exceededCharLimit ? theme.palette.error.main : theme.palette.neutral.main,
                }}
              >
                {textState?.textInputValue?.length} / {maxChars}
              </Typography>
              <LoadingButton
                type="submit"
                loading={loading}
                variant="contained"
                disabled={!canSendMessage}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyItems: "center",
                  borderRadius: "100%",
                  minWidth: 0,
                  width: "44px",
                  height: "44px",
                  padding: 1,
                }}
                aria-label={Locator.feed.input.send}
                onClick={sendTextMessage}
              >
                {!loading ? (
                  <PaperAirplane
                    style={{
                      fill: !canSendMessage ? theme.palette.neutral.main : theme.palette.primary.main,
                      height: "20px",
                      width: "20px",
                    }}
                  />
                ) : null}
              </LoadingButton>
            </Stack>
          </Box>
        </Stack>
      </Box>
      <TemplatesModal open={templatesModal} useSelectedTemplate={handleTemplateSelection} />
    </Stack>
  );
}
